import { useEffect } from 'react';
import { Play, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { TaskStatus, TaskStep } from '../types';
import { useTaskStore } from '../store/taskStore';
import { apiService } from '../services/api';
import clsx from 'clsx';

export function TaskList() {
  const { tasks, updateTask, removeTask } = useTaskStore();

  useEffect(() => {
    // 定期刷新任务状态
    const interval = setInterval(async () => {
      const response = await apiService.getTasks();
      if (response.success && response.data) {
        // 更新本地状态
        response.data.forEach((apiTask) => {
          const localTask = tasks.find((t) => t.id === apiTask.id);
          if (localTask && localTask.status !== apiTask.status) {
            updateTask(apiTask.id, {
              status: apiTask.status,
              currentStep: apiTask.currentStep,
              progress: apiTask.progress,
              error: apiTask.error,
              result: apiTask.result,
            });
          }
        });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [tasks, updateTask]);

  const getStatusIcon = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.COMPLETED:
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case TaskStatus.FAILED:
        return <XCircle className="w-5 h-5 text-red-500" />;
      case TaskStatus.IN_PROGRESS:
        return <Clock className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.COMPLETED:
        return 'bg-green-100 text-green-800 border-green-200';
      case TaskStatus.FAILED:
        return 'bg-red-100 text-red-800 border-red-200';
      case TaskStatus.IN_PROGRESS:
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStepLabel = (step: TaskStep) => {
    const stepLabels: Record<TaskStep, string> = {
      [TaskStep.FETCHING_VIDEO]: 'Fetching Video',
      [TaskStep.ASR_PROCESSING]: 'Speech Recognition',
      [TaskStep.TRANSLATING]: 'Translating',
      [TaskStep.TTS_PROCESSING]: 'Voice Synthesis',
      [TaskStep.VIDEO_SYNTHESIS]: 'Video Synthesis',
      [TaskStep.UPLOADING]: 'Uploading to TikTok',
      [TaskStep.COMPLETED]: 'Completed',
    };
    return stepLabels[step] || step;
  };

  const handleRetry = async (taskId: string) => {
    try {
      const response = await apiService.retryTask(taskId);
      if (response.success) {
        updateTask(taskId, {
          status: TaskStatus.IN_PROGRESS,
          currentStep: TaskStep.FETCHING_VIDEO,
          progress: 0,
          error: undefined,
        });
      }
    } catch (error) {
      console.error('Failed to retry task:', error);
    }
  };

  const handleDelete = (taskId: string) => {
    removeTask(taskId);
  };

  if (tasks.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-12">
          <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">No Tasks Yet</h3>
          <p className="text-gray-500">
            Submit your first task to start localizing videos
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Task List</h2>

      <div className="space-y-4">
        {tasks.map((task) => (
          <div
            key={task.id}
            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
          >
            {/* 任务头部 */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                {getStatusIcon(task.status)}
                <div>
                  <h3 className="font-medium text-gray-900">
                    {task.source === 'douyin' && 'Douyin: '}
                    {task.source === 'user_upload' && 'Upload: '}
                    {task.source === 'url_import' && 'URL: '}
                    {task.sourceKeywords?.join(', ') || task.sourceUrl || 'Unknown'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {new Date(task.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* 状态标签 */}
              <span
                className={clsx(
                  'px-3 py-1 rounded-full text-xs font-medium border',
                  getStatusColor(task.status)
                )}
              >
                {task.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>

            {/* 进度条 */}
            {task.status === TaskStatus.IN_PROGRESS && (
              <div className="mb-3">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{getStepLabel(task.currentStep)}</span>
                  <span className="text-gray-500">{task.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${task.progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* 错误信息 */}
            {task.status === TaskStatus.FAILED && task.error && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{task.error}</p>
              </div>
            )}

            {/* 任务结果 */}
            {task.status === TaskStatus.COMPLETED && task.result && (
              <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 mb-2">Task completed successfully!</p>
                {task.result.tikTokUrl && (
                  <a
                    href={task.result.tikTokUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    View on TikTok
                  </a>
                )}
              </div>
            )}

            {/* 操作按钮 */}
            <div className="flex items-center justify-end space-x-2">
              {task.status === TaskStatus.FAILED && (
                <button
                  onClick={() => handleRetry(task.id)}
                  className="flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Retry
                </button>
              )}
              <button
                onClick={() => handleDelete(task.id)}
                className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}